const OCTA_ATTRIBUTION_STORAGE_KEY = 'octa-events-attribution-v1';
const OCTA_SUCCESS_STORAGE_KEY = 'octa-events-success-v1';
const OCTA_CHOICE_STORAGE_KEY = 'octa-events-choices-v1';
const OCTA_ATTRIBUTION_PARAMETERS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
];

const publishGoogleAdsInquiryConversion = () => {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'conversion', {
    send_to: 'AW-18428248850/CHMdCPC4ku4cEJKGo9NE',
  });
};

const publishGoogleAnalyticsInquiry = (eventData = {}) => {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'generate_lead', {
    event_category: 'inquiry',
    event_label: eventData.eventType || 'event inquiry',
  });
};

const publishOctaEvent = (eventName, eventData = {}, attempt = 0) => {
  const analytics = window.Shopify?.analytics;

  if (typeof analytics?.publish !== 'function') {
    if (attempt < 20) {
      window.setTimeout(() => publishOctaEvent(eventName, eventData, attempt + 1), 250);
    }
    return;
  }

  try {
    const result = analytics.publish(eventName, eventData);
    if (typeof result?.catch === 'function') result.catch(() => {});
  } catch {
    // Analytics must never interrupt the inquiry flow.
  }
};

const cleanAttributionValue = (value) => String(value ?? '').replace(/[\r\n]/g, ' ').trim().slice(0, 250);

const populateAttributionFields = (form) => {
  let storedAttribution = {};

  try {
    storedAttribution = JSON.parse(window.sessionStorage.getItem(OCTA_ATTRIBUTION_STORAGE_KEY) || '{}');
  } catch {
    storedAttribution = {};
  }
  if (!storedAttribution || typeof storedAttribution !== 'object' || Array.isArray(storedAttribution)) {
    storedAttribution = {};
  }

  const query = new URLSearchParams(window.location.search);
  const attribution = { ...storedAttribution };

  OCTA_ATTRIBUTION_PARAMETERS.forEach((parameter) => {
    const queryValue = cleanAttributionValue(query.get(parameter) || '');
    if (queryValue) attribution[parameter] = queryValue;

    const input = form.querySelector(`[data-octa-attribution="${parameter}"]`);
    if (input) input.value = cleanAttributionValue(attribution[parameter] || '');
  });

  try {
    window.sessionStorage.setItem(OCTA_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Attribution still remains in the form when storage is unavailable.
  }
};

const shouldPublishSuccessfulInquiry = () => {
  const successLocation = `${window.location.pathname}${window.location.search}`;

  try {
    if (window.sessionStorage.getItem(OCTA_SUCCESS_STORAGE_KEY) === successLocation) return false;
    window.sessionStorage.setItem(OCTA_SUCCESS_STORAGE_KEY, successLocation);
    return true;
  } catch {
    const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
    return navigationEntry?.type !== 'reload';
  }
};

const resetSuccessfulInquiryMarker = () => {
  try {
    window.sessionStorage.removeItem(OCTA_SUCCESS_STORAGE_KEY);
  } catch {
    // A fresh inquiry still works when session storage is unavailable.
  }
};

const readSelectedChoice = (form, fieldName) => form.querySelector(`input[name="${fieldName}"]:checked`)?.value || '';

const storeChoiceState = (form) => {
  const choices = {
    eventType: readSelectedChoice(form, 'contact[Typ eventu]'),
    guestCount: form.querySelector('[data-octa-guest-count]')?.value.trim() || '',
  };

  try {
    window.sessionStorage.setItem(OCTA_CHOICE_STORAGE_KEY, JSON.stringify(choices));
  } catch {
    // The form remains usable without restoring choices after a server error.
  }
};

const restoreChoiceState = (form) => {
  try {
    const choices = JSON.parse(window.sessionStorage.getItem(OCTA_CHOICE_STORAGE_KEY) || '{}');
    const matchingEvent = Array.from(form.querySelectorAll('.octa-choice-grid input')).find(
      (input) => input.value === choices.eventType,
    );
    if (matchingEvent) matchingEvent.checked = true;

    const guestCountInput = form.querySelector('[data-octa-guest-count]');
    if (guestCountInput && choices.guestCount) guestCountInput.value = choices.guestCount;
  } catch {
    // Invalid or unavailable session storage is ignored.
  }
};

const populateInquiryPrefill = (form, venueSelect) => {
  const query = new URLSearchParams(window.location.search);
  const requestedVenue = cleanAttributionValue(query.get('venue') || '');
  const requestedEvent = cleanAttributionValue(query.get('event') || '');

  if (requestedVenue && venueSelect) {
    const matchingVenue = Array.from(venueSelect.options).find((option) => option.value === requestedVenue);
    if (matchingVenue) venueSelect.value = matchingVenue.value;
  }

  if (requestedEvent) {
    const matchingEvent = Array.from(form.querySelectorAll('input[name="contact[Typ eventu]"]')).find(
      (input) => input.value === requestedEvent,
    );
    if (matchingEvent) matchingEvent.checked = true;
  }
};

const clearChoiceState = () => {
  try {
    window.sessionStorage.removeItem(OCTA_CHOICE_STORAGE_KEY);
  } catch {
    // Nothing to clear when session storage is unavailable.
  }
};

const initializeOctaEvents = (scope = document) => {
  scope.querySelectorAll('[data-octa-events]').forEach((root) => {
    if (root.dataset.octaEventsInitialized === 'true') return;
    root.dataset.octaEventsInitialized = 'true';

    const form = root.querySelector('.octa-lead-form');
    const leadCard = root.querySelector('.octa-lead-card');
    const venueSelect = root.querySelector('[data-octa-venue-select]');
    const venueField = root.querySelector('[data-octa-venue-field]');
    const cateringFields = root.querySelector('[data-octa-catering-fields]');
    const eventOnlyFields = root.querySelectorAll('[data-octa-event-only]');
    const analyticsContext = {
      form_id: 'octa_event_inquiry',
      page_path: window.location.pathname,
    };

    const scrollToForm = () => {
      leadCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    root.querySelectorAll('[data-octa-scroll-form]').forEach((button) => {
      button.addEventListener('click', scrollToForm);
    });

    if (leadCard && 'IntersectionObserver' in window) {
      const formVisibilityObserver = new IntersectionObserver(
        ([entry]) => root.classList.toggle('octa-form-is-visible', entry.isIntersecting),
        { threshold: 0.08 },
      );
      formVisibilityObserver.observe(leadCard);
    }

    root.querySelectorAll('[data-octa-phone-cta]').forEach((link) => {
      link.addEventListener('click', () => {
        publishOctaEvent('octa_event_phone_cta_clicked', {
          ...analyticsContext,
          placement: link.dataset.octaPhoneCta || 'unknown',
        });
      });
    });

    root.querySelectorAll('[data-octa-venue]').forEach((button) => {
      button.addEventListener('click', () => {
        const requestedVenue = button.dataset.octaVenue || '';
        const matchingOption = Array.from(venueSelect?.options || []).find(
          (option) => option.value.toLowerCase() === requestedVenue.toLowerCase(),
        );
        if (matchingOption && venueSelect) venueSelect.value = matchingOption.value;
        publishOctaEvent('octa_event_venue_cta_clicked', {
          ...analyticsContext,
          venue: requestedVenue,
        });
        scrollToForm();
      });
    });

    root.querySelectorAll('[data-octa-catering]').forEach((button) => {
      button.addEventListener('click', () => {
        const cateringChoice = form?.querySelector('input[name="contact[Typ eventu]"][value="Externý catering"]');
        if (cateringChoice) {
          cateringChoice.checked = true;
          cateringChoice.dispatchEvent(new Event('change', { bubbles: true }));
        }
        publishOctaEvent('octa_event_catering_cta_clicked', analyticsContext);
        scrollToForm();
      });
    });

    if (!form) return;

    populateAttributionFields(form);

    const successState = form.querySelector('[data-octa-success]');
    if (successState && !successState.hidden) {
      window.setTimeout(() => successState.focus(), 0);
      clearChoiceState();
      if (shouldPublishSuccessfulInquiry()) {
        publishOctaEvent('octa_event_inquiry_step_completed', {
          ...analyticsContext,
          step: 2,
        });
        publishOctaEvent('octa_event_inquiry_submitted', analyticsContext);
      }
      return;
    }

    resetSuccessfulInquiryMarker();

    const stepOne = form.querySelector('[data-octa-step="1"]');
    const stepTwo = form.querySelector('[data-octa-step="2"]');
    const nextButton = form.querySelector('[data-octa-next]');
    const previousButton = form.querySelector('[data-octa-previous]');
    const stepCount = root.querySelector('[data-octa-step-count]');
    const progress = root.querySelector('[data-octa-progress]');
    const stepError = form.querySelector('[data-octa-step-error]');
    const completedSteps = new Set();
    let formStarted = false;

    restoreChoiceState(form);
    populateInquiryPrefill(form, venueSelect);

    const trackFormStart = () => {
      if (formStarted) return;
      formStarted = true;
      publishOctaEvent('octa_event_inquiry_started', analyticsContext);
    };

    const trackStepCompleted = (step) => {
      if (completedSteps.has(step)) return;
      completedSteps.add(step);
      publishOctaEvent('octa_event_inquiry_step_completed', {
        ...analyticsContext,
        step,
      });
    };

    const setStep = (step, shouldFocus = true) => {
      const isSecondStep = step === 2;
      stepOne.hidden = isSecondStep;
      stepTwo.hidden = !isSecondStep;
      if (stepCount) stepCount.textContent = String(step);
      progress?.classList.toggle('is-complete', isSecondStep);
      if (isSecondStep && shouldFocus) {
        window.setTimeout(() => stepTwo.querySelector('input, select')?.focus(), 180);
      }
    };

    const syncChoiceState = () => {
      form.querySelectorAll('.octa-choice-grid label').forEach((label) => {
        label.classList.toggle('is-selected', Boolean(label.querySelector('input:checked')));
      });
      const guestCount = Number(form.querySelector('[data-octa-guest-count]')?.value || 0);
      const ready = Boolean(
        form.querySelector('input[name="contact[Typ eventu]"]:checked') &&
          Number.isInteger(guestCount) &&
          guestCount > 0 &&
          guestCount <= 9999,
      );
      if (nextButton) nextButton.disabled = !ready;
      if (ready && stepError) stepError.hidden = true;
      const isCatering = readSelectedChoice(form, 'contact[Typ eventu]') === 'Externý catering';
      venueField?.toggleAttribute('hidden', isCatering);
      eventOnlyFields.forEach((field) => field.toggleAttribute('hidden', isCatering));
      if (venueSelect) {
        venueSelect.required = !isCatering;
        venueSelect.disabled = isCatering;
        if (isCatering) venueSelect.value = '';
      }
      if (cateringFields) {
        cateringFields.hidden = !isCatering;
        cateringFields.querySelectorAll('[data-octa-catering-required]').forEach((field) => {
          field.required = isCatering;
          field.disabled = !isCatering;
        });
      }
      form.classList.toggle('is-catering-inquiry', isCatering);
      storeChoiceState(form);
    };

    form.addEventListener('focusin', trackFormStart);
    form.addEventListener('input', () => {
      trackFormStart();
      syncChoiceState();
    });
    form.addEventListener('change', () => {
      trackFormStart();
      syncChoiceState();
    });
    nextButton?.addEventListener('click', () => {
      if (nextButton.disabled) {
        if (stepError) stepError.hidden = false;
        return;
      }
      trackFormStart();
      trackStepCompleted(1);
      setStep(2);
    });
    previousButton?.addEventListener('click', () => setStep(1));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const submitButton = form.querySelector('button[type="submit"]');
      const submitError = form.querySelector('[data-octa-submit-error]');
      if (submitError) submitError.hidden = true;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.dataset.originalLabel = submitButton.innerHTML;
        submitButton.textContent = 'Odosielam…';
      }

      try {
        const formData = new FormData(form);
        const customerEmail = form.querySelector('input[name="contact[email]"]')?.value.trim() || '';
        const customerName = form.querySelector('input[name="contact[name]"]')?.value.trim() || '';
        formData.set('email', customerEmail);
        formData.set('name', customerName);

        const response = await fetch('https://octa-events-mail.radoslav-truhan.workers.dev/', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const result = await response.json();
        if (!response.ok || result.success !== true) throw new Error('Inquiry delivery failed');

        stepOne.hidden = true;
        stepTwo.hidden = true;
        form.querySelector('.octa-progress')?.setAttribute('hidden', '');
        if (successState) {
          successState.hidden = false;
          successState.focus();
        }
        clearChoiceState();
        publishOctaEvent('octa_event_inquiry_step_completed', { ...analyticsContext, step: 2 });
        publishOctaEvent('octa_event_inquiry_submitted', analyticsContext);
        publishGoogleAnalyticsInquiry(analyticsContext);
        publishGoogleAdsInquiryConversion();
      } catch {
        if (submitError) submitError.hidden = false;
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.innerHTML = submitButton.dataset.originalLabel || 'Odoslať dopyt';
        }
      }
    });

    const startsOnStepTwo = Boolean(stepOne?.hidden && !stepTwo?.hidden);
    setStep(startsOnStepTwo ? 2 : 1, false);
    syncChoiceState();
  });
};

document.addEventListener('DOMContentLoaded', () => initializeOctaEvents());
document.addEventListener('shopify:section:load', (event) => initializeOctaEvents(event.target));
